/**
 * Shared blacksmith UI assets and panel surfaces.
 */

/** API basic recipe row — only fields used for list ordering. */
export type BlacksmithBasicRecipeOrder = {
  weaponId: string
  isLocked?: boolean
  requiredLevel?: number
}

/** Unlocked (level) recipes first, then locked; then by `requiredLevel`, then `weaponId`. */
export function sortBlacksmithBasicRecipesUnlockedFirst(
  a: BlacksmithBasicRecipeOrder,
  b: BlacksmithBasicRecipeOrder
): number {
  const aLocked = !!a.isLocked
  const bLocked = !!b.isLocked
  if (aLocked !== bLocked) return aLocked ? 1 : -1
  const ar = a.requiredLevel ?? 1
  const br = b.requiredLevel ?? 1
  if (ar !== br) return ar - br
  return a.weaponId.localeCompare(b.weaponId)
}

/**
 * Format a craft duration (in hours, possibly fractional — e.g. 3.6667 = 3h40m) as a compact label.
 * Craft times now step in 20-minute increments (max 4h, −20m per blacksmith upgrade), so hours alone
 * would read as "3.6667h"; show whole hours + minutes instead ("4h", "3h 40m", "40m").
 */
export function formatCraftTimeLabel(hours: number): string {
  const totalMinutes = Math.max(0, Math.round((Number(hours) || 0) * 60))
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  if (h > 0 && m > 0) return `${h}h ${m}m`
  if (h > 0) return `${h}h`
  return `${m}m`
}

export const BLACKSMITH_VIEW_PANEL_BG_URL = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/craft_items/blacksmith/view-panel-bg.webp'

/**
 * Dark charcoal “stone slab” panel with subtle mottle (matches parchment + crafting modal reference).
 * Use as `background` on LandPageModal or inline `background` style.
 */
export const BLACKSMITH_MODAL_BACKGROUND = `
  radial-gradient(ellipse 130% 90% at 15% -5%, rgba(88, 82, 74, 0.32), transparent 52%),
  radial-gradient(ellipse 100% 70% at 95% 100%, rgba(0, 0, 0, 0.32), transparent 48%),
  radial-gradient(ellipse 60% 45% at 50% 50%, rgba(45, 42, 38, 0.2), transparent 70%),
  linear-gradient(168deg, rgba(44, 41, 38, 0.82) 0%, rgba(30, 28, 26, 0.84) 42%, rgba(22, 20, 18, 0.86) 100%)
`

/** Slight wash so UI text stays readable on top of `BLACKSMITH_VIEW_PANEL_BG_URL` */
export const BLACKSMITH_EQUIPMENT_VIEW_OVERLAY =
  'linear-gradient(to bottom, rgba(14, 12, 10, 0.28) 0%, rgba(12, 10, 9, 0.42) 55%, rgba(10, 9, 8, 0.52) 100%)'

/**
 * Weapon art for the **center equipment view panel** only (`blacksmith-page` parchment preview).
 * Add files under `public/assets/weapons/` named `<weaponId>.webp` (same id as recipe `weaponId`).
 * Left-column equipment tiles keep API `recipe.weaponImg`; do not point them here.
 */
export function blacksmithEquipmentViewImageUrl(weaponId: string): string {
  const id = (weaponId || '').trim().toLowerCase().replace(/\s+/g, '_')
  return `https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/craft_items/weapons/${id}.webp`
}

/** Craft / cancel / immediate buttons on blacksmith page — `public/assets/button/*.webp` */
export const BLACKSMITH_CRAFT_BTN_BG = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/craft_items/button/craft_btn.webp'
export const BLACKSMITH_CANCEL_CRAFT_BTN_BG = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/craft_items/button/cancel_craft_btn.webp'
export const BLACKSMITH_IMMEDIATE_CRAFT_BTN_BG = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/craft_items/button/immediate_craft_btn.webp'
